import { useUiContext } from '@elementary/common-ui/components/AiChat';
import { UseFormReturn } from 'react-hook-form';
import useSelectedWarehouseType from 'shared/hooks/useSelectedWarehouseType';
import { AddTestForm } from 'shared/types';
import {
  EditTestMetadata,
  WizardFieldIssue,
  WizardMode,
  WizardResourceType,
  WizardSelectableTest,
  WizardStageInfo,
} from 'testManagement/types';
import {
  getWizardFieldLockReason,
  getWizardFieldOwner,
  getWizardStage,
  getWritableWizardFields,
  extractWizardSeverityFromYaml,
  getUnselectableWizardResourcesReason,
  getStageValidationError,
  getWizardWriteFieldError,
  isStageValid,
  isWizardTestSelectionWrite,
  normalizeWizardWriteArgs,
  normalizeWizardWriteFieldValue,
  parseWizardWriteForm,
  resolveWizardTestSelection,
  setWizardFormField,
  WIZARD_BLOCKED_FIELDS,
} from 'testManagement/utils/utils';
import {
  getWizardTimestampFieldOptions,
  normalizeWizardTimestampFieldWrite,
} from 'testManagement/utils/wizardTimestampFields';
import { WIZARD_YAML_CHAR_LIMIT } from './constants';
import { previewWizardPageUpdate } from './utils';

type Props = {
  methods: UseFormReturn<AddTestForm>;
  stage: number;
  wizardStages: WizardStageInfo[];
  wizardMode: WizardMode;
  wizardResourceType: WizardResourceType;
  editTestId?: string;
  editTestMetadata?: EditTestMetadata;
  selectableTests: WizardSelectableTest[];
  selectableResourceIds?: string[];
  isConfigOnlyFlow: boolean;
  isTestConfigEdited: boolean;
  isTicketRequired: boolean;
  timestampColumns: string[];
};

const useWizardUiContext = ({
  methods,
  stage,
  wizardStages,
  wizardMode,
  wizardResourceType,
  editTestId,
  editTestMetadata,
  selectableTests,
  selectableResourceIds,
  isConfigOnlyFlow,
  isTestConfigEdited,
  isTicketRequired,
  timestampColumns,
}: Props) => {
  const currentWizardStage = getWizardStage(wizardStages, stage);
  const warehouseType = useSelectedWarehouseType();
  const writableWizardFields = getWritableWizardFields({
    methods,
    stage,
    wizardStages,
    wizardMode,
    timestampColumns,
  });

  useUiContext({
    read_test_config_wizard: {
      description:
        'Read the open add/edit/plan test wizard: current step (with its meaning), all steps for this mode, live unsaved form values, writableFields for the visible step, lockedFields (fields on this step the user cannot change right now, with the reason), timestampFieldOptions (the only values timestampColumn / updatedAtTimestampColumn accept for the selected test; allowsNone means "" is allowed for no column), and testTemplates (selectable tests and the exact testType / yamlTemplateName keys to write). Custom SQL is returned in full. Not available unless this wizard is open.',
      handler: () => {
        const form = methods.getValues();
        const yaml = form.yaml;
        const truncated =
          typeof yaml === 'string' && yaml.length > WIZARD_YAML_CHAR_LIMIT;
        return {
          wizardMode,
          wizardResourceType,
          stage: currentWizardStage,
          stages: wizardStages,
          editTestId: editTestId ?? null,
          editedTestMetadata: editTestMetadata ?? null,
          dirty: methods.formState.isDirty,
          truncated,
          writableFields: writableWizardFields.fields,
          lockedFields: writableWizardFields.lockedFields,
          writableFieldsDegraded: writableWizardFields.usedMountedFallback,
          timestampFieldOptions: getWizardTimestampFieldOptions(
            form.testType,
            timestampColumns,
          ),
          testTemplates: selectableTests,
          form: {
            ...form,
            yaml: truncated ? yaml.slice(0, WIZARD_YAML_CHAR_LIMIT) : yaml,
          },
        };
      },
    },
    write_test_config_wizard: {
      description:
        'Propose values for the currently visible test wizard step. Only fields listed in writableFields from read_test_config_wizard can be set. writableFields matches the controls the user can change on this step. resources must be ids of tables the wizard lists. get_table_assets also returns assets the wizard does not list: Elementary\'s own package models (ids containing model.elementary. or a schema ending in _elementary), seeds, snapshots, and assets that cannot have tests. Never choose those as targets, even when the user asks for random or all tables; pick from the user\'s own models and sources instead. When writableFields includes yaml, form.yaml is only the test parameters, such as combination_of_columns and quote_columns. Set severity, owners, tags, shouldStoreFailures, description, whereClause, and testNameOverride as those form fields. Do not put store_failures, tags, meta, error_if, or warn_if under config in the yaml. Conditional severity is form.severity: { type: "error" or "warn", conditionalError: { operator: ">", value: 1 }, conditionalWarning?: { operator, value } }. type may be "conditional". error_if / errorIf / warn_if / warnIf strings such as ">1" are also accepted. yaml is not writable while editing an existing test. For freshness, event freshness, volume, and dimension anomaly tests, the time bucket is detectionPeriod + detectionUnit (hour, day, week, or month; no month on Redshift) and the comparison window is trainingPeriodCount + trainingPeriodUnit (day, week, or month), e.g. "last 14 days" is trainingPeriodCount: 14, trainingPeriodUnit: "day". The comparison window must cover at least 7 time buckets; when the step is invalid, stageError explains why. timestampColumn and updatedAtTimestampColumn only accept values from timestampFieldOptions in the read tool; use an empty string for no column when allowsNone is true, and do not write fields listed in lockedFields. On the test step, testType and yamlTemplateName must match a testTemplates entry from the read tool. Do not write a testTemplates entry that has disabledReason; explain that reason instead. Does not navigate steps or submit, the user must manually navigate to the next step. Requires user approval.',
      requiresApproval: true,
      preview: previewWizardPageUpdate,
      argsSchema: {
        type: 'object',
        properties: {
          form: { type: ['object', 'string'] },
          reason: { type: 'string' },
        },
      },
      handler: (args) => {
        const proposed = parseWizardWriteForm(
          normalizeWizardWriteArgs(args).form,
        );
        if (!proposed) {
          return {
            applied: [],
            rejected: [
              {
                field: 'form',
                reason: 'form must be a JSON object',
              },
            ],
            stage: currentWizardStage,
            stageValid: isStageValid(
              methods,
              stage,
              isConfigOnlyFlow,
              isTestConfigEdited,
              isTicketRequired,
            ),
            stageError: getStageValidationError(
              methods,
              stage,
              isConfigOnlyFlow,
              timestampColumns,
            ),
            dirty: methods.formState.isDirty,
            hint: currentWizardStage.hint,
            writableFields: writableWizardFields.fields,
            lockedFields: writableWizardFields.lockedFields,
            writableFieldsDegraded: writableWizardFields.usedMountedFallback,
          };
        }
        const writable = new Set(writableWizardFields.fields);
        const applied: string[] = [];
        const rejected: WizardFieldIssue[] = [];
        const skipFields = new Set<string>();

        if (
          isWizardTestSelectionWrite(proposed) &&
          (writable.has('testType') || writable.has('yamlTemplateName'))
        ) {
          if (selectableTests.length === 0) {
            rejected.push({
              field: 'testType',
              reason: 'The test catalog is not available yet',
            });
            skipFields.add('testType');
            skipFields.add('yamlTemplateName');
          } else {
            const resolved = resolveWizardTestSelection(
              proposed,
              selectableTests,
            );
            if (!resolved.ok) {
              rejected.push({ field: 'testType', reason: resolved.reason });
              skipFields.add('testType');
              skipFields.add('yamlTemplateName');
            } else {
              methods.setValue('testType', resolved.testType, {
                shouldDirty: true,
                shouldValidate: true,
              });
              methods.setValue('yamlTemplateName', resolved.yamlTemplateName, {
                shouldDirty: true,
                shouldValidate: true,
              });
              if (writable.has('testType')) {
                applied.push('testType');
              }
              if (writable.has('yamlTemplateName')) {
                applied.push('yamlTemplateName');
              }
              skipFields.add('testType');
              skipFields.add('yamlTemplateName');
            }
          }
        }

        Object.entries(proposed).forEach(([field, value]) => {
          if (skipFields.has(field)) {
            return;
          }
          const lockReason = getWizardFieldLockReason({
            field,
            methods,
            wizardMode,
            timestampColumns,
          });
          if (lockReason) {
            rejected.push({
              field,
              reason: lockReason,
            });
            return;
          }
          if (WIZARD_BLOCKED_FIELDS.some((blocked) => blocked === field)) {
            rejected.push({
              field,
              reason: 'Set by the wizard after validation, not writable',
            });
            return;
          }
          if (!writable.has(field)) {
            const owner = getWizardFieldOwner(field, wizardStages);
            rejected.push({
              field,
              reason: `Owned by step ${owner.index} (${owner.name})`,
            });
            return;
          }
          if (field === 'yaml' && typeof value !== 'string') {
            rejected.push({
              field,
              reason: 'yaml must be a string',
            });
            return;
          }
          const fieldError = getWizardWriteFieldError(field, value);
          if (fieldError) {
            rejected.push({ field, reason: fieldError });
            return;
          }
          const timestampWrite = normalizeWizardTimestampFieldWrite({
            field,
            value,
            testType: methods.getValues('testType'),
            timestampColumns,
          });
          if (!timestampWrite.ok) {
            rejected.push({ field, reason: timestampWrite.reason });
            return;
          }
          const normalized = normalizeWizardWriteFieldValue(
            field,
            timestampWrite.value,
            methods.getValues(field as never),
            warehouseType,
          );
          if (!normalized.ok) {
            rejected.push({ field, reason: normalized.reason });
            return;
          }
          const unselectableReason =
            field === 'resources' && selectableResourceIds
              ? getUnselectableWizardResourcesReason(
                  normalized.value,
                  selectableResourceIds,
                )
              : undefined;
          if (unselectableReason) {
            rejected.push({ field, reason: unselectableReason });
            return;
          }
          setWizardFormField(methods, field, normalized.value);
          applied.push(field);
          if (
            field === 'yaml' &&
            typeof value === 'string' &&
            writable.has('severity') &&
            !Object.prototype.hasOwnProperty.call(proposed, 'severity')
          ) {
            const liftedSeverity = extractWizardSeverityFromYaml(value);
            if (liftedSeverity) {
              setWizardFormField(methods, 'severity', liftedSeverity);
              applied.push('severity');
            }
          }
        });

        return {
          applied,
          rejected,
          stage: currentWizardStage,
          stageValid: isStageValid(
            methods,
            stage,
            isConfigOnlyFlow,
            isTestConfigEdited,
            isTicketRequired,
          ),
          stageError: getStageValidationError(
            methods,
            stage,
            isConfigOnlyFlow,
            timestampColumns,
          ),
          dirty: methods.formState.isDirty,
          hint: currentWizardStage.hint,
          writableFields: writableWizardFields.fields,
          lockedFields: writableWizardFields.lockedFields,
          writableFieldsDegraded: writableWizardFields.usedMountedFallback,
        };
      },
    },
  });
};

export default useWizardUiContext;
